import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { Icon, ExternalIcon, ArrowForwardSVGpath } from '@skatteetaten/ds-icons';
import { Spinner } from '@skatteetaten/ds-progress';
import './styles.css';


const getNavigationTileHeadingAsDefault = ()=>'h2';
const getNavigationTileHideArrowDefault = ()=>false;
const getNavigationTileSizeDefault = ()=>'large';

var styles = {
    "icon": "NavigationTile-module_icon__657D9",
    "icon_large": "NavigationTile-module_icon_large__aikZT",
    "icon_extraLarge": "NavigationTile-module_icon_extraLarge__t8pLE",
    "spinner": "NavigationTile-module_spinner__lUzR4",
    "spinner_extraLarge": "NavigationTile-module_spinner_extraLarge__W7sDW",
    "header": "NavigationTile-module_header__4mf6h",
    "title": "NavigationTile-module_title__ezDhE",
    "title_large": "NavigationTile-module_title_large__7rif5",
    "title_extraLarge": "NavigationTile-module_title_extraLarge__iDIu2",
    "description": "NavigationTile-module_description__n1GSn",
    "description_extraLarge": "NavigationTile-module_description_extraLarge__WRhFi",
    "descriptionIndented": "NavigationTile-module_descriptionIndented__iC-Ev",
    "navigationTile": "NavigationTile-module_navigationTile__HeuYv",
    "navigationTile_extraLarge": "NavigationTile-module_navigationTile_extraLarge__q-nNM",
    "navigationTile_medium": "NavigationTile-module_navigationTile_medium__h46Hl",
    "navigationTile_large": "NavigationTile-module_navigationTile_large__EIIOc"
};

const NavigationTile = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, title, titleAs: TitleTag = getNavigationTileHeadingAsDefault(), classNames, description, hasSpinner, isExternal, hideArrowIcon = getNavigationTileHideArrowDefault(), size = getNavigationTileSizeDefault(), spinnerTitle, svgPath, href, target, ariaDescribedby, onClick }, ref)=>{
    const { t } = useTranslation('ds_buttons', {
        i18n: dsI18n
    });
    const navTileRef = useRef(null);
    useImperativeHandle(ref, ()=>navTileRef.current);
    useEffect(()=>{
        const navTile = navTileRef.current;
        if (!navTile) return;
        // hvis konsument sender inn onClick, men ikke href, har NavigationTile samme oppførsel som en knapp, og bør kunne trigges med tastatur.
        const handleKeyDown = (event)=>{
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                navTile.click();
            }
        };
        if (!onClick) return;
        navTile.addEventListener('keydown', handleKeyDown);
        return ()=>{
            navTile.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        onClick
    ]);
    const iconClassNames = `${styles.icon} ${size !== 'medium' ? styles[`icon_${size}`] : ''}`.trim();
    const spinnerClassNames = `${styles.spinner} ${size === 'extraLarge' ? styles[`spinner_${size}`] : ''}`.trim();
    const titleClassNames = `${styles.title} ${size !== 'medium' ? styles[`title_${size}`] : ''} ${classNames?.title}`.trim();
    const descriptionClassNames = `${styles.description} ${size === 'extraLarge' && styles.description_extraLarge} ${size !== 'extraLarge' && (svgPath || hasSpinner) ? styles.descriptionIndented : ''} ${classNames?.description ?? ''}`.trim();
    const concatenatedClassName = `${styles.navigationTile} ${styles[`navigationTile_${size}`]} ${className} ${classNames?.container ?? ''}`.trim();
    return /*#__PURE__*/ jsxs("a", {
        ref: navTileRef,
        id: id,
        className: concatenatedClassName,
        lang: lang,
        "data-testid": dataTestId,
        href: href,
        rel: target === '_blank' ? 'noreferrer' : undefined,
        target: target,
        "aria-describedby": ariaDescribedby,
        onClick: onClick,
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: styles.header,
                children: [
                    svgPath && !hasSpinner && /*#__PURE__*/ jsx(Icon, {
                        size: size === 'extraLarge' ? 'extraLarge' : 'large',
                        svgPath: svgPath,
                        className: iconClassNames
                    }),
                    hasSpinner && /*#__PURE__*/ jsx(Spinner, {
                        className: spinnerClassNames,
                        color: 'blue',
                        size: size === 'extraLarge' ? 'large' : 'medium',
                        hideTitle: true,
                        children: spinnerTitle
                    }),
                    /*#__PURE__*/ jsx(TitleTag, {
                        className: titleClassNames,
                        children: title
                    }),
                    isExternal && size !== 'extraLarge' ? /*#__PURE__*/ jsx(ExternalIcon, {
                        size: 'large',
                        className: iconClassNames,
                        ariaLabel: t('shared.ExternalIcon')
                    }) : null,
                    !isExternal && !hideArrowIcon && size !== 'extraLarge' ? /*#__PURE__*/ jsx(Icon, {
                        size: 'large',
                        svgPath: ArrowForwardSVGpath,
                        className: iconClassNames
                    }) : null
                ]
            }),
            description && /*#__PURE__*/ jsx("div", {
                className: descriptionClassNames,
                children: description
            })
        ]
    });
});
NavigationTile.displayName = 'NavigationTile';

export { NavigationTile, getNavigationTileHeadingAsDefault, getNavigationTileHideArrowDefault, getNavigationTileSizeDefault };
